import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement, BIMElement } from '../../core/types';
import { computeWallMoveUpdates, createTranslatedWallCopy } from '../../utils/WallGeometryUtils';

/**
 * Offset Tool - BIM 2025 Style
 * 
 * Moves or copies an element a specified distance perpendicular to its length.
 * Works on walls, lines, detail lines, etc.
 * 
 * Workflow:
 * 1. Select element to offset (or pre-select)
 * 2. Click side to offset towards
 * 3. Drag or type distance
 */
export class OffsetToolClass extends BaseTool {
    id = 'OFFSET';
    name = 'Offset';
    cursor = 'crosshair';

    private sourceElement: BIMElement | null = null;
    private offsetSide: 'left' | 'right' | null = null;
    private previewOffset: number = 0;
    private copyMode: boolean = false; // If true, creates copy; if false, moves original

    activate(context: any): void {
        super.activate(context);

        // If elements are pre-selected, use the first one
        const selectedIds = this.context?.appStore.selectedIds || [];
        if (selectedIds.length > 0) {
            const id = selectedIds[0];
            const element = this.context?.bimStore.project.elements.get(id);
            if (element && this.isOffsetable(element)) {
                this.sourceElement = element;
                console.log('Offset: Using pre-selected element', id);
            }
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        if (!this.sourceElement) {
            // Select element to offset
            const hit = this.getOffsetableIntersection(event);
            if (hit) {
                this.sourceElement = hit.element;
                console.log('Offset: Source element selected', this.sourceElement.id);
            }
            return;
        }

        if (!this.offsetSide) {
            // Determine which side to offset based on click position
            this.offsetSide = this.determineSide(point);
            console.log('Offset: Side selected', this.offsetSide);
            return;
        }

        // Third click = finalize offset
        const distance = this.calculateOffsetDistance(point);
        this.applyOffset(distance);

        // Reset
        this.reset();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.sourceElement || !this.offsetSide) return;

        const point = this.getIntersection(event);
        if (!point) return;

        // Calculate preview offset distance
        this.previewOffset = this.calculateOffsetDistance(point);

        // Update preview
        this.updatePreview();
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        super.onKeyDown(event);

        // Toggle copy mode with Ctrl
        if (event.key === 'Control') {
            this.copyMode = !this.copyMode;
            console.log('Offset: Copy mode', this.copyMode ? 'ON' : 'OFF');
        }

        // Cancel with Escape
        if (event.key === 'Escape') {
            this.reset();
        }
    }

    private isOffsetable(element: BIMElement): boolean {
        const offsetableCategories = ['Walls', 'Lines', 'Detail Lines', 'Model Lines'];
        return offsetableCategories.includes(element.category);
    }

    private getOffsetableIntersection(event: ThreeEvent<PointerEvent>) {
        const hit = this.findElementHitAtPoint(event, (el) => this.isOffsetable(el));
        if (!hit) return null;
        return { element: hit.element, point: hit.point };
    }

    private determineSide(clickPoint: THREE.Vector3): 'left' | 'right' {
        if (!this.sourceElement) return 'right';

        if (this.sourceElement.category === 'Walls') {
            const wall = this.sourceElement as WallElement;
            const start = new THREE.Vector3(...wall.geometryData.start);
            const end = new THREE.Vector3(...wall.geometryData.end);

            // Wall direction
            const wallDir = end.clone().sub(start).normalize();

            // Perpendicular direction (in XY plane, assuming Z is up)
            const perpDir = new THREE.Vector3(-wallDir.y, wallDir.x, 0);

            // Vector from wall midpoint to click
            const midpoint = start.clone().add(end).multiplyScalar(0.5);
            const toClick = clickPoint.clone().sub(midpoint);

            // Dot product to determine side
            const dot = toClick.dot(perpDir);
            return dot >= 0 ? 'right' : 'left';
        }

        return 'right';
    }

    private calculateOffsetDistance(point: THREE.Vector3): number {
        if (!this.sourceElement) return 0;

        if (this.sourceElement.category === 'Walls') {
            const wall = this.sourceElement as WallElement;
            const start = new THREE.Vector3(...wall.geometryData.start);
            const end = new THREE.Vector3(...wall.geometryData.end);

            // Project point onto perpendicular line
            const line = new THREE.Line3(start, end);
            const closestOnLine = new THREE.Vector3();
            line.closestPointToPoint(point, true, closestOnLine);

            const distance = point.distanceTo(closestOnLine);

            // Snap to grid if close
            const snappedDistance = Math.round(distance * 2) / 2; // Snap to 0.5 increments
            return snappedDistance;
        }

        return 0;
    }

    private updatePreview(): void {
        if (!this.context || !this.sourceElement) return;

        // Set preview data for rendering
        this.context.appStore.setToolPreview({
            active: true,
            toolId: this.id,
            offset: this.previewOffset,
            side: this.offsetSide,
            sourceId: this.sourceElement.id,
            copyMode: this.copyMode
        });
    }

    private applyOffset(distance: number): void {
        if (!this.context || !this.sourceElement || !this.offsetSide) return;

        if (this.sourceElement.category === 'Walls') {
            const wall = this.sourceElement as WallElement;
            const start = new THREE.Vector3(...wall.geometryData.start);
            const end = new THREE.Vector3(...wall.geometryData.end);

            // Calculate perpendicular direction
            const wallDir = end.clone().sub(start).normalize();
            const perpDir = new THREE.Vector3(-wallDir.y, wallDir.x, 0);

            // Apply direction based on side
            const direction = this.offsetSide === 'right' ? 1 : -1;
            const offsetVector = perpDir.multiplyScalar(distance * direction);

            if (this.copyMode) {
                // Copy: fresh render geometry, joins left behind
                const newWall = createTranslatedWallCopy(wall, offsetVector);
                this.context.bimStore.addElement(newWall);
                console.log('Offset: Created new wall at distance', distance);
            } else {
                // Parallel offset is a disjoin-move: connected walls stay put
                // (no stretching) but their miters at the vacated joints must
                // revert to clean ends — handled by the joint-aware solver.
                const wallUpdates = computeWallMoveUpdates(
                    this.context.bimStore.project.elements,
                    [wall.id],
                    offsetVector,
                    { stretchConnected: false }
                );
                const batch: Array<{ id: string; updates: Partial<BIMElement> }> = [];
                wallUpdates.forEach((updates, id) => batch.push({ id, updates }));
                if (batch.length > 0) {
                    this.context.bimStore.updateElements(batch);
                }
                console.log('Offset: Moved wall by', distance);
            }
        }
    }

    private reset(): void {
        this.sourceElement = null;
        this.offsetSide = null;
        this.previewOffset = 0;
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
    }
}
